import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
  ComposedChart
} from 'recharts';
import { TrendingUp, AlertCircle, DollarSign, Package, ArrowLeft, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [complaintsData, setComplaintsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchComplaintsData();
  }, []);

  const fetchComplaintsData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getComplaints();
      const dataArray = Array.isArray(data) ? data : [];
      setComplaintsData(dataArray);
      setError(null);
    } catch (err) {
      console.error('Error fetching complaints:', err);
      setError(err.message);
      setComplaintsData([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics from API data
  const totalComplaints = complaintsData.length;
  const totalCost = complaintsData.reduce((sum, item) => sum + parseFloat(item['Cost Estimate (USD)'] || 0), 0);
  const openComplaints = complaintsData.filter(item => item.Status?.toLowerCase() === 'open').length;
  const reportedComplaints = complaintsData.filter(item => item.Status?.toLowerCase() === 'reported').length;
  const highSeverity = complaintsData.filter(item => item['Severity Level']?.toLowerCase() === 'high').length;
  const mediumSeverity = complaintsData.filter(item => item['Severity Level']?.toLowerCase() === 'medium').length;
  const lowSeverity = complaintsData.filter(item => item['Severity Level']?.toLowerCase() === 'low').length;

  // Severity Distribution
  const severityData = [
    { name: 'High', value: highSeverity, color: '#ef4444' },
    { name: 'Medium', value: mediumSeverity, color: '#f59e0b' },
    { name: 'Low', value: lowSeverity, color: '#10b981' }
  ].filter(item => item.value > 0);

  // Status Distribution
  const statusData = complaintsData.reduce((acc, item) => {
    const status = item.Status || 'Unknown';
    const existing = acc.find(d => d.name === status);
    if (existing) {
      existing.count += 1;
    } else {
      acc.push({ name: status, count: 1 });
    }
    return acc;
  }, []);

  // Product Complaints
  const productData = complaintsData.reduce((acc, item) => {
    const product = item.Product || 'Unknown';
    const cost = parseFloat(item['Cost Estimate (USD)'] || 0);
    const existing = acc.find(d => d.name === product);
    if (existing) {
      existing.complaints += 1;
      existing.totalCost += cost;
    } else {
      acc.push({ name: product, complaints: 1, totalCost: cost });
    }
    return acc;
  }, []).sort((a, b) => b.complaints - a.complaints);

  // Business Impact Distribution
  const impactData = complaintsData.reduce((acc, item) => {
    const impact = item['Business Impact'] || 'Unknown';
    const cost = parseFloat(item['Cost Estimate (USD)'] || 0);
    const existing = acc.find(d => d.name === impact);
    if (existing) {
      existing.value += 1;
      existing.cost += cost;
    } else {
      acc.push({ name: impact, value: 1, cost: cost });
    }
    return acc;
  }, []);

  // Monthly Trend
  const monthlyData = complaintsData.reduce((acc, item) => {
    if (!item['Production Date']) return acc;
    const date = new Date(item['Production Date']);
    if (isNaN(date.getTime())) return acc;
    const month = date.toLocaleString('default', { month: 'short', year: 'numeric' });
    const existing = acc.find(d => d.month === month);
    if (existing) {
      existing.complaints += 1;
      existing.cost += parseFloat(item['Cost Estimate (USD)'] || 0);
    } else {
      acc.push({ month, complaints: 1, cost: parseFloat(item['Cost Estimate (USD)'] || 0) });
    }
    return acc;
  }, []).sort((a, b) => {
    const dateA = new Date(a.month);
    const dateB = new Date(b.month);
    return dateA - dateB;
  });

  // Batch Analysis
  const batchData = complaintsData.reduce((acc, item) => {
    const batch = item.Batch || 'Unknown';
    const cost = parseFloat(item['Cost Estimate (USD)'] || 0);
    const existing = acc.find(d => d.name === batch);
    if (existing) {
      existing.count += 1;
      existing.totalCost += cost;
    } else {
      acc.push({ name: batch, count: 1, totalCost: cost });
    }
    return acc;
  }, []).sort((a, b) => b.count - a.count).slice(0, 10);

  // Customer Analysis
  const customerData = complaintsData.reduce((acc, item) => {
    const customerId = item['Customer ID'] || 'Unknown';
    const existing = acc.find(d => d.id === customerId);
    if (existing) {
      existing.complaints += 1;
    } else {
      acc.push({ id: customerId, complaints: 1 });
    }
    return acc;
  }, []).sort((a, b) => b.complaints - a.complaints).slice(0, 10);

  // Defect Causes Analysis
  const defectCauses = complaintsData.reduce((acc, item) => {
    const description = item['Defect Description'] || 'Unknown';
    const words = description.toLowerCase().split(' ');
    
    // Categorize defects
    let category = 'Other';
    if (words.some(w => ['crack', 'broken', 'damage', 'physical'].includes(w))) category = 'Physical Damage';
    else if (words.some(w => ['loose', 'missing', 'part'].includes(w))) category = 'Missing Parts';
    else if (words.some(w => ['service', 'maintenance'].includes(w))) category = 'Service';
    else if (words.some(w => ['machine', 'equipment'].includes(w))) category = 'Machine Issue';
    
    const existing = acc.find(d => d.name === category);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ name: category, value: 1 });
    }
    return acc;
  }, []);

  // Production volume by product (simulated based on complaints)
  const productionVolume = productData.slice(0, 5).map((product, idx) => ({
    name: product.name,
    volume: product.complaints * 1000,
    percentage: totalComplaints > 0 ? ((product.complaints / totalComplaints) * 100).toFixed(0) : 0
  }));

  // Daily/Weekly trend
  const dailyTrend = complaintsData.reduce((acc, item) => {
    if (!item['Production Date']) return acc;
    const date = new Date(item['Production Date']);
    if (isNaN(date.getTime())) return acc;
    const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const existing = acc.find(d => d.day === day);
    if (existing) {
      existing.count += 1;
    } else {
      acc.push({ day, count: 1 });
    }
    return acc;
  }, []).slice(-20);

  // Cost distribution by severity
  const costBySeverity = [
    { 
      name: 'High', 
      cost: complaintsData
        .filter(c => c['Severity Level']?.toLowerCase() === 'high')
        .reduce((sum, c) => sum + parseFloat(c['Cost Estimate (USD)'] || 0), 0)
    },
    { 
      name: 'Medium', 
      cost: complaintsData
        .filter(c => c['Severity Level']?.toLowerCase() === 'medium')
        .reduce((sum, c) => sum + parseFloat(c['Cost Estimate (USD)'] || 0), 0)
    },
    { 
      name: 'Low', 
      cost: complaintsData
        .filter(c => c['Severity Level']?.toLowerCase() === 'low')
        .reduce((sum, c) => sum + parseFloat(c['Cost Estimate (USD)'] || 0), 0)
    }
  ].filter(item => item.cost > 0);

  // Repeat defects analysis
  const repeatDefects = complaintsData.filter(c => c['Is Repeat Defect']).length;
  const repeatRate = totalComplaints > 0 ? ((repeatDefects / totalComplaints) * 100).toFixed(1) : 0;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchComplaintsData}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Product Manufacturing Dashboard
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  Comprehensive analysis of complaints and quality metrics
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* KPI Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6"
        >
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs font-medium mb-1">Total Complaints</p>
                <h3 className="text-3xl font-bold text-gray-900">{totalComplaints}</h3>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <Package className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs font-medium mb-1">Open Issues</p>
                <h3 className="text-3xl font-bold text-gray-900">{openComplaints}</h3>
                <p className="text-xs text-gray-500 mt-1">Reported: {reportedComplaints}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <AlertCircle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs font-medium mb-1">Total Cost</p>
                <h3 className="text-3xl font-bold text-gray-900">${totalCost.toFixed(0)}</h3>
                <p className="text-xs text-gray-500 mt-1">Avg: ${totalComplaints > 0 ? (totalCost / totalComplaints).toFixed(0) : 0}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs font-medium mb-1">High Severity</p>
                <h3 className="text-3xl font-bold text-gray-900">{highSeverity}</h3>
                <p className="text-xs text-gray-500 mt-1">{totalComplaints > 0 ? ((highSeverity / totalComplaints) * 100).toFixed(1) : 0}% of total</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <TrendingUp className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Additional Manufacturing Metrics */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6"
        >
          {/* Defect Causes - Donut Chart */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Defect Causes Analysis</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={defectCauses}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={false}
                >
                  {defectCauses.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#06b6d4', '#10b981', '#f59e0b', '#8b5cf6'][index % 4]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-center -mt-32 pointer-events-none mb-20">
              <div className="text-2xl font-bold text-gray-900">{totalComplaints}</div>
              <div className="text-xs text-gray-500">Total Defects</div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {defectCauses.map((cause, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-gray-600">{cause.name}</span>
                  <span className="font-semibold">{totalComplaints > 0 ? ((cause.value / totalComplaints) * 100).toFixed(0) : 0}%</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Production Statistics Panel */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Production Statistics</h3>
            <div className="space-y-4">
              {/* Production Volume */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">{(totalComplaints * 1000).toLocaleString()}</div>
                  <div className="text-xs text-gray-500">Production volume</div>
                </div>
                <div className="w-32 h-12">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyData.slice(-8)}>
                      <Line
                        type="monotone"
                        dataKey="complaints"
                        stroke="#06b6d4"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">{productData.length}</div>
                  <div className="text-xs text-gray-500">Active products</div>
                </div>
              </div>

              {/* Order Volume */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">{totalComplaints.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">Total complaints</div>
                </div>
                <div className="w-32 h-12">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyTrend.slice(-10)}>
                      <Bar dataKey="count" fill="#06b6d4" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">${(totalCost / 1000).toFixed(1)}k</div>
                  <div className="text-xs text-gray-500">Total cost</div>
                </div>
              </div>

              {/* Production Volume Record */}
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="text-xs text-gray-700 font-semibold mb-3">Production volume by product</div>
                <div className="grid grid-cols-5 gap-2">
                  {productionVolume.map((item, idx) => (
                    <div key={idx} className="text-center">
                      <div className="relative w-12 h-12 mx-auto mb-2">
                        <svg className="w-full h-full transform -rotate-90">
                          <circle
                            cx="24"
                            cy="24"
                            r="20"
                            stroke={idx % 2 === 0 ? '#fbbf24' : '#06b6d4'}
                            strokeWidth="4"
                            fill="none"
                            strokeDasharray={`${parseInt(item.percentage) * 1.26} 126`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-gray-900">{item.percentage}%</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 truncate">{item.name.substring(0, 8)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Daily Complaint Trend */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Daily Complaint Trend</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="day" stroke="#9ca3af" fontSize={9} angle={-45} textAnchor="end" height={60} />
                <YAxis stroke="#9ca3af" fontSize={10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }}
                />
                <Bar dataKey="count" fill="#fbbf24" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Cost Distribution by Severity */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Cost Distribution by Severity</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={costBySeverity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(value) => `$${value.toFixed(2)}`}
                />
                <Bar dataKey="cost" radius={[8, 8, 0, 0]}>
                  {costBySeverity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.name === 'High' ? '#ef4444' : entry.name === 'Medium' ? '#f59e0b' : '#10b981'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Top Products Table with Sparklines */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6 lg:col-span-2">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Top Products Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 bg-yellow-100 font-semibold text-gray-700">Product Name</th>
                    <th className="text-center py-2 px-3 bg-cyan-100 font-semibold text-gray-700">Complaints</th>
                    <th className="text-center py-2 px-3 bg-cyan-200 font-semibold text-gray-700">Total Cost</th>
                    <th className="text-center py-2 px-3 bg-cyan-300 font-semibold text-gray-700">Avg Cost</th>
                    <th className="text-center py-2 px-3 bg-cyan-400 font-semibold text-gray-700">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {productData.slice(0, 5).map((product, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="py-3 px-3 bg-yellow-50 font-medium text-gray-900">{product.name}</td>
                      <td className="py-3 px-3 text-center text-gray-700">{product.complaints}</td>
                      <td className="py-3 px-3 text-center text-gray-700">${product.totalCost.toFixed(2)}</td>
                      <td className="py-3 px-3 text-center text-gray-700">${(product.totalCost / product.complaints).toFixed(2)}</td>
                      <td className="py-3 px-3">
                        <div className="w-20 h-8 mx-auto">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={[
                              { value: product.complaints * 0.8 },
                              { value: product.complaints * 0.9 },
                              { value: product.complaints * 1.1 },
                              { value: product.complaints }
                            ]}>
                              <Line 
                                type="monotone" 
                                dataKey="value" 
                                stroke={['#fbbf24', '#06b6d4', '#10b981', '#8b5cf6', '#ec4899'][idx % 5]} 
                                strokeWidth={2} 
                                dot={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Quality Metrics Cards */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Quality Metrics</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-xs text-gray-600 mb-2">Defect Rate</div>
                <div className="text-3xl font-bold text-red-600">
                  {totalComplaints > 0 ? ((highSeverity / totalComplaints) * 100).toFixed(1) : 0}%
                </div>
                <div className="text-xs text-gray-500 mt-1">High severity</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-xs text-gray-600 mb-2">Quality Rate</div>
                <div className="text-3xl font-bold text-green-600">
                  {totalComplaints > 0 ? (100 - ((highSeverity / totalComplaints) * 100)).toFixed(1) : 100}%
                </div>
                <div className="text-xs text-gray-500 mt-1">Acceptable</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-xs text-gray-600 mb-2">Repeat Rate</div>
                <div className="text-3xl font-bold text-orange-600">{repeatRate}%</div>
                <div className="text-xs text-gray-500 mt-1">{repeatDefects} repeats</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-xs text-gray-600 mb-2">Resolution Rate</div>
                <div className="text-3xl font-bold text-blue-600">
                  {totalComplaints > 0 ? (((totalComplaints - openComplaints) / totalComplaints) * 100).toFixed(1) : 0}%
                </div>
                <div className="text-xs text-gray-500 mt-1">Resolved</div>
              </div>
            </div>
          </motion.div>

          {/* Batch Performance */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Batch Performance Overview</h3>
            <div className="space-y-3">
              {batchData.slice(0, 6).map((batch, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-gray-900">{batch.name}</div>
                    <div className="text-xs text-gray-500">{batch.count} complaints</div>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          batch.count > 3 ? 'bg-red-500' : 
                          batch.count > 1 ? 'bg-orange-400' : 
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min((batch.count / Math.max(...batchData.map(b => b.count))) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold text-gray-900">${batch.totalCost.toFixed(0)}</div>
                    <div className="text-xs text-gray-500">cost</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Main Charts Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-5"
        >
          {/* Severity Distribution - Donut Chart */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Severity Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={severityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {severityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-2 mt-4 text-xs text-center">
              <div>
                <div className="font-bold text-red-600">{highSeverity}</div>
                <div className="text-gray-500">High</div>
              </div>
              <div>
                <div className="font-bold text-orange-600">{mediumSeverity}</div>
                <div className="text-gray-500">Medium</div>
              </div>
              <div>
                <div className="font-bold text-green-600">{lowSeverity}</div>
                <div className="text-gray-500">Low</div>
              </div>
            </div>
          </motion.div>

          {/* Status Overview - Bar Chart */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Status Overview</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="count" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Product Complaints - Horizontal Bar */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Complaints by Product</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={productData.slice(0, 8)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" stroke="#9ca3af" fontSize={11} />
                <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={10} width={100} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="complaints" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Business Impact - Composed Chart */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Business Impact Analysis</h3>
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={impactData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} angle={-15} textAnchor="end" height={80} />
                <YAxis yAxisId="left" stroke="#9ca3af" fontSize={11} />
                <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar yAxisId="left" dataKey="value" name="Count" fill="#06b6d4" radius={[8, 8, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="cost" name="Cost ($)" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444', r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Monthly Trend - Area Chart */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6 lg:col-span-2">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Monthly Complaint Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="colorComplaints" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Area type="monotone" dataKey="complaints" stroke="#8b5cf6" fill="url(#colorComplaints)" name="Complaints" strokeWidth={2} />
                <Area type="monotone" dataKey="cost" stroke="#10b981" fill="none" name="Cost ($)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Batch Analysis - Bar Chart */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Top Batches by Complaints</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={batchData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} angle={-15} textAnchor="end" height={60} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="count" fill="#f59e0b" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Product Cost Analysis */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Product Cost Impact</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={productData.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} angle={-15} textAnchor="end" height={60} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(value) => `$${value.toFixed(2)}`}
                />
                <Bar dataKey="totalCost" fill="#ec4899" radius={[8, 8, 0, 0]} name="Total Cost ($)" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Top Customers by Complaints */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Top Customers by Complaints</h3>
            <div className="space-y-3">
              {customerData.map((customer, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-purple-600">{idx + 1}</span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Customer {customer.id}</div>
                      <div className="text-xs text-gray-500">{customer.complaints} complaints</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900">{customer.complaints}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Summary Statistics */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-gray-700 font-semibold text-sm mb-4">Summary Statistics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <span className="text-sm text-gray-700">Total Products Affected</span>
                <span className="text-lg font-bold text-purple-600">{productData.length}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <span className="text-sm text-gray-700">Total Batches Affected</span>
                <span className="text-lg font-bold text-orange-600">{batchData.length}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm text-gray-700">Avg Cost per Complaint</span>
                <span className="text-lg font-bold text-green-600">${totalComplaints > 0 ? (totalCost / totalComplaints).toFixed(2) : 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="text-sm text-gray-700">Unique Customers</span>
                <span className="text-lg font-bold text-blue-600">{customerData.length}</span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-6 pb-6">
          Dashboard updates automatically based on real-time complaint data
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
